import { DomainValidationError } from "./identifiers.js";
// Where a concierge request began (CHT-749, contract 7.1). Staff read it to trace a request back to the
// conversation or the screen it came from. It is set when the draft is inserted and never changes
// (the database guard LF054). A row from before CHT-749 has no source (`null`: unknown).
// Frozen: the database CHECK constraint is generated from this tuple.
export const conciergeRequestSources = Object.freeze([
    "CHAT",
    "CARD",
    "ITINERARY_ITEM",
    "ACTION_CENTER",
    "STAFF",
]);
/** What a customer route may claim. `CHAT` is the agent's alone and `STAFF` is the staff console's alone. */
export const customerRouteRequestSources = Object.freeze([
    "CARD",
    "ITINERARY_ITEM",
    "ACTION_CENTER",
]);
/** The most characters of a Stream message id. The database CHECK uses the same bound. */
export const SOURCE_MESSAGE_REF_MAX = 128;
const SOURCE_MESSAGE_REF = /^[A-Za-z0-9_.:-]{1,128}$/;
/** A Stream message id as stored: visible ASCII only, so it can never carry text of a customer. */
export function requestSourceMessageRef(value, field = "sourceMessageRef") {
    if (typeof value !== "string" || !SOURCE_MESSAGE_REF.test(value)) {
        throw new DomainValidationError(field, "must be a message id");
    }
    return value;
}
/**
 * The provenance of a new request. The caller decides it, never the model and never the body alone:
 *  - the concierge agent channel is `CHAT` and must name the message of the customer that it acts on;
 *  - the app may claim a card, an itinerary item or the Action Center, and `ITINERARY_ITEM` needs the item link;
 *  - staff are `STAFF`.
 * A claim that does not fit the caller is a validation error, so a customer cannot pass as the agent or as staff.
 */
export function resolveRequestProvenance(input) {
    const { actor } = input;
    const claimed = input.source ?? null;
    const ref = input.sourceMessageRef ?? null;
    if (actor === "staff") {
        if (claimed !== null && claimed !== "STAFF") {
            throw new DomainValidationError("source", "must be STAFF for a staff request");
        }
        if (ref !== null)
            throw new DomainValidationError("sourceMessageRef", "is for chat only");
        return { source: "STAFF", sourceMessageRef: null };
    }
    if (actor === "concierge-agent") {
        if (claimed !== null && claimed !== "CHAT") {
            throw new DomainValidationError("source", "must be CHAT for the concierge agent");
        }
        if (ref === null) {
            if (input.requireChatMessageRef === false)
                return { source: "CHAT", sourceMessageRef: null };
            throw new DomainValidationError("sourceMessageRef", "is required for a chat request");
        }
        return { source: "CHAT", sourceMessageRef: requestSourceMessageRef(ref) };
    }
    const source = claimed ?? "ACTION_CENTER";
    if (!customerRouteRequestSources.includes(source)) {
        throw new DomainValidationError("source", "is not allowed for the app");
    }
    if (ref !== null)
        throw new DomainValidationError("sourceMessageRef", "is for chat only");
    if (source === "ITINERARY_ITEM" && (input.itineraryItemId ?? null) === null) {
        throw new DomainValidationError("source", "ITINERARY_ITEM needs an itinerary item link");
    }
    return { source: source, sourceMessageRef: null };
}
/**
 * The zone a wall time of a request is read in (CHT-749, contract 7.1). It comes from the place the request is about, in this order:
 * the linked itinerary item, then the linked catalog item, then the customer's explicit text (a zone they or the agent named for the
 * place they named). It is NEVER the device's zone: no route, tool or DTO takes one. A zone of a linked row wins over an explicit one,
 * because the wall time is the venue's ("dinner at 19:30" is 19:30 where the dinner is).
 */
export function resolveRequestTimeZone(input) {
    return input.itineraryItemTimeZone ?? input.catalogItemTimeZone ?? input.explicit ?? null;
}
